import { useCallback, useEffect } from 'react';
import { useParams, useSearchParams } from '@direct-frontend/modules-router';
import { trackEcomEvent } from '@/entities/trackEcomEvent';
import { trackGoal } from '@/entities/trackGoal';
import { type OrderDto } from '@/shared/api/order';
import { getRequestContext } from '@/shared/lib/requestContext';

interface UseTrackCheckoutParams {
    order?: OrderDto | null;
}

export const useTrackCheckout = ({ order }: UseTrackCheckoutParams) => {
    const { storeSlug = '' } = useParams();

    const [searchParams] = useSearchParams();
    const metricClientId = searchParams.get('metricClientId') ?? '';
    const from = searchParams.get('from') ?? '';

    const { company } = getRequestContext();
    const { companyTitle, merchantYandexCounter } = company;

    //Трекаем в метрику открытие чекаута
    useEffect(() => {
        trackGoal('checkout_open', { referer: document?.referrer });
    }, [merchantYandexCounter, storeSlug]);

    const trackEvents = useCallback(async () => {
        if (!merchantYandexCounter) {
            return;
        }

        if (from !== 'shop') {
            await trackEcomEvent('pageview', metricClientId, merchantYandexCounter, storeSlug, {
                title: companyTitle || window.location.hostname,
                eventName: 'pageview',
            });

            await trackEcomEvent('ecom', metricClientId, merchantYandexCounter, storeSlug, {
                title: companyTitle || window.location.hostname,
                eventName: 'add',
                products: (order?.delivery_chunks ?? []).flatMap((chunk) =>
                    chunk.items.map((item) => {
                        return {
                            id: item.product_variant_id || item.sku,
                            name: [item.name, item.sku].filter(Boolean).join(' '),
                            brand: item.brand,
                            price: item.price,
                        };
                    })
                ),
            });
        }

        if (merchantYandexCounter) {
            trackEcomEvent('ecom', metricClientId, merchantYandexCounter, storeSlug, {
                title: companyTitle || window.location.hostname,
                eventName: 'checkout',
            });
        }
    }, [companyTitle, from, merchantYandexCounter, metricClientId, order?.delivery_chunks, storeSlug]);

    useEffect(() => {
        if (metricClientId && merchantYandexCounter && storeSlug && order) {
            trackEvents();
        }
    }, [merchantYandexCounter, metricClientId, order, storeSlug, trackEvents]);
};
